<script setup lang="ts">
/**
 * ADMIN-T013 / ADMIN-T017 (D12-B) — side menu with permission-based item filtering.
 * Items with a `perm` field are hidden when the current actor lacks that permission.
 * Filtering is done in a computed (not in template v-if) to avoid the no-permission-bypass
 * ESLint rule, which flags raw permission checks in directive expressions.
 */
import type { Component } from 'vue';
import { computed, ref, watch } from 'vue';
import {
  BarChart3,
  BellRing,
  BriefcaseBusiness,
  ChevronDown,
  ClipboardList,
  Database,
  Settings,
} from '@lucide/vue';
import { RouterLink, useRoute } from 'vue-router';
import { usePermissionStore } from '@/permission/store';

interface MenuItem {
  label: string;
  to: string;
  /** Permission code required to show this item. Omit to always show. */
  perm?: string;
  /** Additional route matcher for detail/edit pages that belong to this menu item. */
  activeWhen?: (path: string) => boolean;
}

interface MenuGroup {
  label: string;
  icon: Component;
  defaultOpen?: boolean;
  items: MenuItem[];
}

const groups: MenuGroup[] = [
  {
    label: '业务录入',
    icon: Database,
    defaultOpen: true,
    items: [
      {
        label: '服务提醒单录入',
        to: '/admin/service-reminders/new',
        perm: 'service_reminder:write',
      },
      {
        label: '新建生产单',
        to: '/admin/presales-production-notices/new',
        perm: 'presales_notice:write',
      },
      {
        label: '员工录入',
        to: '/admin/employees/new',
        perm: 'employee:write',
        activeWhen: (path) => path === '/admin/employees/new',
      },
    ],
  },
  {
    label: '数据管理',
    icon: BriefcaseBusiness,
    defaultOpen: true,
    items: [
      { label: '提醒单台账', to: '/admin/service-reminders', perm: 'service_reminder:read' },
      {
        label: '项目合同',
        to: '/admin/project-contracts',
        perm: 'project_contract:read',
        activeWhen: (path) => path.startsWith('/admin/project-contracts/'),
      },
      {
        label: '维保项目',
        to: '/admin/maintenance-projects',
        perm: 'maintenance_project:read',
        activeWhen: (path) => path.startsWith('/admin/maintenance-projects/'),
      },
      {
        label: '客户主数据',
        to: '/admin/master-customers',
        perm: 'master_customer:read',
        activeWhen: (path) => path.startsWith('/admin/master-customers/'),
      },
      {
        label: '设备主数据',
        to: '/admin/master-devices',
        perm: 'master_device:read',
        activeWhen: (path) => path.startsWith('/admin/master-devices/'),
      },
      { label: '工单主数据', to: '/admin/work-order-master', perm: 'work_order_master:read' },
      { label: '主数据同步', to: '/admin/master-data/sync-jobs', perm: 'master_data:sync:read' },
      { label: '同步隔离记录', to: '/admin/master-data/rejections', perm: 'master_data:sync:read' },
      {
        label: '员工管理',
        to: '/admin/employees',
        perm: 'employee:read',
        activeWhen: (path) => path === '/admin/employees',
      },
      // ADMIN-T359 / CORE-T262: 平台用户管理 (admin-only user:read). ADMIN-T360 / CORE-T263:
      // 角色管理 (admin-only role:read) — read/edit the local role matrix. Both admin-only.
      { label: '平台用户管理', to: '/admin/user', perm: 'user:read' },
      { label: '角色管理', to: '/admin/role', perm: 'role:read' },
      { label: '报告/配件流转', to: '/admin/shipments', perm: 'shipment:read' },
      { label: '财务协同', to: '/admin/finance', perm: 'finance:read' },
      { label: '周报', to: '/admin/weekly', perm: 'weekly:read' },
      { label: '导出中心', to: '/admin/export-center', perm: 'export:read' },
      { label: '审计', to: '/admin/audit', perm: 'audit:read' },
    ],
  },
  {
    label: '售前交付',
    icon: ClipboardList,
    defaultOpen: true,
    items: [
      {
        label: '生产通知单',
        to: '/admin/presales-production-notices',
        perm: 'presales_notice:read',
        activeWhen: (path) =>
          path.startsWith('/admin/presales-production-notices/') &&
          path !== '/admin/presales-production-notices/new',
      },
      {
        label: '自动分派规则',
        to: '/admin/presales-assignment-rules',
        perm: 'presales_assignment_rule:read',
      },
      { label: '人工成本参数', to: '/admin/presales-cost-rates', perm: 'presales_cost:read' },
    ],
  },
  {
    label: '预警规则',
    icon: BellRing,
    items: [
      { label: '预警发送预览', to: '/admin/alerts/preview', perm: 'alerts:read' },
      { label: '提醒意图日志', to: '/admin/reminders', perm: 'reminder:read' },
      {
        label: '服务号消息记录',
        to: '/admin/notification-deliveries',
        perm: 'notification_delivery:read',
      },
    ],
  },
  {
    label: '配置',
    icon: Settings,
    items: [
      {
        label: '售后全局兜底',
        to: '/admin/config/aftersales-global-fallback',
        perm: 'aftersales_responsibility:read',
      },
      {
        label: '分级提醒时间',
        to: '/admin/config/aftersales-stall-policy',
        perm: 'config:aftersales_stall:read',
      },
      {
        label: '工单策略配置',
        to: '/admin/config/work-order-policy',
        perm: 'config:work_order_policy:read',
      },
      {
        label: '通知模板配置',
        to: '/admin/config/notification-templates',
        perm: 'config:notification_template:read',
      },
      {
        label: '导出模板配置',
        to: '/admin/config/export-templates',
        perm: 'config:export_template:read',
      },
      // ADMIN-T380: 甲方接口状态 (Party-A integration status, mock contract). Admin-only via
      // integration:read (held through the admin `*`); hidden from non-admin roles.
      { label: '甲方接口状态', to: '/admin/integration/party-a', perm: 'integration:read' },
    ],
  },
  {
    label: '数据中台',
    icon: BarChart3,
    items: [
      { label: '驾驶舱', to: '/dashboard/cockpit', perm: 'dashboard:cockpit:read' },
      { label: '员工画像', to: '/dashboard/profile', perm: 'dashboard:profile:read' },
      { label: '档案与报告', to: '/dashboard/archive', perm: 'dashboard:archive:read' },
      { label: '响应分析', to: '/dashboard/response', perm: 'dashboard:response:read' },
      { label: '质保 BI', to: '/dashboard/warranty-bi', perm: 'dashboard:warranty:read' },
      { label: '财务 BI', to: '/dashboard/finance-bi', perm: 'dashboard:finance:read' },
    ],
  },
];

const store = usePermissionStore();
const route = useRoute();
const openGroups = ref<Set<string>>(new Set());
const manuallyClosedGroups = ref<Set<string>>(new Set());

const visibleGroups = computed(() =>
  groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => !item.perm || store.hasPermission(item.perm)),
    }))
    .filter((g) => g.items.length > 0),
);

function isActive(item: MenuItem): boolean {
  const path = route.path;
  return path === item.to || Boolean(item.activeWhen?.(path));
}

function groupHasActiveItem(group: MenuGroup): boolean {
  return group.items.some((item) => isActive(item));
}

watch(
  [visibleGroups, () => route.path],
  ([nextGroups]) => {
    if (nextGroups.length === 0) {
      openGroups.value = new Set();
      return;
    }

    const next = new Set(openGroups.value);
    for (const group of nextGroups) {
      if (
        !manuallyClosedGroups.value.has(group.label) &&
        (group.defaultOpen || groupHasActiveItem(group))
      ) {
        next.add(group.label);
      }
    }
    if (next.size === 0) {
      next.add(nextGroups[0]!.label);
    }
    openGroups.value = next;
  },
  { immediate: true },
);

function isGroupOpen(group: MenuGroup): boolean {
  return openGroups.value.has(group.label);
}

function toggleGroup(label: string): void {
  const next = new Set(openGroups.value);
  const nextManuallyClosed = new Set(manuallyClosedGroups.value);
  if (next.has(label)) {
    next.delete(label);
    nextManuallyClosed.add(label);
  } else {
    next.add(label);
    nextManuallyClosed.delete(label);
  }
  openGroups.value = next;
  manuallyClosedGroups.value = nextManuallyClosed;
}
</script>

<template>
  <nav
    class="side-menu"
    aria-label="主导航"
  >
    <div
      v-for="g in visibleGroups"
      :key="g.label"
      class="group"
      :class="{ open: isGroupOpen(g), active: groupHasActiveItem(g) }"
    >
      <button
        class="group-trigger"
        type="button"
        :aria-expanded="isGroupOpen(g)"
        @click="toggleGroup(g.label)"
      >
        <span class="group-title">
          <component
            :is="g.icon"
            class="group-icon"
            :size="17"
          />
          <span>{{ g.label }}</span>
        </span>
        <ChevronDown
          class="chevron"
          :size="16"
          aria-hidden="true"
        />
      </button>
      <ul v-show="isGroupOpen(g)">
        <li
          v-for="item in g.items"
          :key="item.to"
        >
          <RouterLink
            :to="item.to"
            exact-active-class="active"
            :class="{ active: isActive(item) }"
          >
            {{ item.label }}
          </RouterLink>
        </li>
      </ul>
    </div>
  </nav>
</template>

<style scoped>
.side-menu {
  width: 248px;
  flex: 0 0 248px;
  height: 100%;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  padding: 14px 12px;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.group {
  border-radius: 8px;
}
.group + .group {
  margin-top: 6px;
}
.group-trigger {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #334155;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.16s ease,
    color 0.16s ease;
}
.group-trigger:hover {
  background: #f1f5f9;
  color: #1677ff;
}
.group.active > .group-trigger {
  color: #0958d9;
}
.group-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.group-icon {
  flex: 0 0 auto;
}
.chevron {
  flex: 0 0 auto;
  color: #94a3b8;
  transition: transform 0.16s ease;
}
.group.open .chevron {
  transform: rotate(180deg);
}
ul {
  list-style: none;
  padding: 3px 0 6px;
  margin: 0;
}
li a {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 34px;
  margin: 2px 0;
  padding: 7px 10px 7px 38px;
  color: #475569;
  text-decoration: none;
  font-size: 13px;
  border-radius: 7px;
  transition:
    background 0.16s ease,
    color 0.16s ease;
}
li a::before {
  content: '';
  position: absolute;
  left: 22px;
  top: 10px;
  bottom: 10px;
  width: 2px;
  border-radius: 999px;
  background: #d8dee8;
}
li a:hover {
  background: #f1f5f9;
  color: #1677ff;
}
li a.active {
  background: #e6f4ff;
  color: #0958d9;
  font-weight: 600;
}
li a.active::before {
  background: #1677ff;
}
.side-menu::-webkit-scrollbar {
  width: 8px;
}
.side-menu::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
.side-menu::-webkit-scrollbar-track {
  background: transparent;
}
</style>
